import { http } from '@pequity/shared/axios/http';
import { expandParameterMap } from '@pequity/shared/utils/url';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, type Ref } from 'vue';

export type MarketDataVersion = {
  id: number;
  name: string;
};

export type MarketDataDisplayLayout = {
  id: number;
  name: string;
  display_name: string;
  version: number;
  type: 'boolean_value' | 'string_value' | 'text_value' | 'date_value' | 'integer_value' | 'decimal_value';
  editable: boolean;
  searchable: boolean;
  filterable: boolean;
  sortable: boolean;
  order: number;
  visible: boolean;
  tooltip: string | null;
  required: boolean;
  border_color: string | null;
};

export type MarketDataJobData = {
  id: number;
  version: number;
  version_name: string | null;
  job_code: string;
  data: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
};

type PaginatedResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

export const fetchMarketDataVersions = async (): Promise<MarketDataVersion[]> => {
  const response = await http.get<MarketDataVersion[]>('market-data/versions');
  return response.data;
};

export const useMarketDataVersionsQuery = () => {
  return useQuery({
    queryKey: ['market-data', 'versions'],
    queryFn: fetchMarketDataVersions,
    staleTime: 1_500,
  });
};

export const useMarketDataDisplayLayoutsQuery = (versionId: Ref<number | null>) => {
  return useQuery({
    queryKey: ['market-data', 'display-layouts', versionId],
    queryFn: async () => {
      const response = await http.get<MarketDataDisplayLayout[]>('market-data/display-layouts', {
        params: {
          version: versionId.value,
        },
      });
      return response.data;
    },
    enabled: computed(() => Boolean(versionId.value)),
  });
};

type JobDataFilters = Record<string, string[]>;

export type JobDataQueryParams = {
  page: number;
  page_size: number;
  version: number | null;
  search?: string;
  ordering?: string[];
  filters?: JobDataFilters;
};

export const useMarketDataJobDataQuery = (params: Ref<JobDataQueryParams>) => {
  return useQuery({
    queryKey: ['market-data', 'job-data', params],
    queryFn: async () => {
      const filterParams = Object.entries(params.value.filters ?? {}).reduce(
        (acc, [key, value]) => {
          acc[key] = value;
          return acc;
        },
        {} as Record<string, string[]>
      );

      const queryParams = {
        page: params.value.page,
        page_size: params.value.page_size,
        version: params.value.version,
        search: params.value.search,
        ordering: params.value.ordering?.join(','),
        ...filterParams,
      };

      const response = await http.get<PaginatedResponse<MarketDataJobData>>('market-data/job-data', {
        params: expandParameterMap(queryParams),
      });
      return response.data;
    },
    enabled: computed(() => Boolean(params.value.version)),
    placeholderData: keepPreviousData,
  });
};

type MarketDataFilterOptionsResponse = Record<string, string[]>;

export const useMarketDataJobDataFiltersQuery = (versionId: Ref<number | null>) => {
  return useQuery({
    queryKey: ['market-data', 'job-data', 'filters', versionId],
    queryFn: async () => {
      const response = await http.get<MarketDataFilterOptionsResponse>('market-data/job-data/filters', {
        params: {
          version: versionId.value,
        },
      });
      return response.data;
    },
    enabled: computed(() => Boolean(versionId.value)),
  });
};

export type DisplayLayoutUpdatePayload = Partial<MarketDataDisplayLayout> & { id: number };

export const usePatchDisplayLayoutMutation = (versionId: Ref<number | null>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: DisplayLayoutUpdatePayload) => {
      const response = await http.patch<MarketDataDisplayLayout>(`market-data/display-layouts/${payload.id}/`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['market-data', 'display-layouts', versionId] });
    },
  });
};

type SaveDisplayLayoutOrderPayload = {
  layoutId: number;
  newOrder: number;
};

export const useSaveDisplayLayoutOrderMutation = (versionId: Ref<number | null>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SaveDisplayLayoutOrderPayload) => {
      const response = await http.patch('market-data/display-layouts/update_order/', {
        layout_id: payload.layoutId,
        new_order: payload.newOrder,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['market-data', 'display-layouts', versionId] });
    },
  });
};

type VersionUpdatePayload = {
  id: number;
  name: string;
};

export const useUpdateVersionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: VersionUpdatePayload) => {
      const response = await http.patch<MarketDataVersion>(`market-data/versions/${payload.id}/`, {
        name: payload.name,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['market-data', 'versions'] });
    },
  });
};

export const useDeleteVersionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (versionId: number) => {
      await http.delete(`market-data/versions/${versionId}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['market-data', 'versions'] });
    },
  });
};

type CreateVersionFromDataPayload = {
  name: string;
  job_code_field: string;
  schema_definition: Record<string, { type: string; unique: boolean; allowEmpty: boolean }>;
  rows: Record<string, unknown>[];
};

const buildCsvBlob = (rows: Record<string, unknown>[]): Blob => {
  if (rows.length === 0) return new Blob([''], { type: 'text/csv' });

  const headers = Object.keys(rows[0]);
  const escapeCsvValue = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const lines = [headers.map(escapeCsvValue).join(',')];
  for (const row of rows) {
    lines.push(headers.map((h) => escapeCsvValue(row[h])).join(','));
  }
  return new Blob([lines.join('\n')], { type: 'text/csv' });
};

export const useCreateVersionFromDataMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateVersionFromDataPayload) => {
      const formData = new FormData();
      formData.append('name', payload.name);
      formData.append('job_code_field', payload.job_code_field);
      formData.append('schema_definition', JSON.stringify(payload.schema_definition));
      formData.append('file', buildCsvBlob(payload.rows), 'data.csv');

      const response = await http.post<MarketDataVersion>('market-data/versions/create_from_data/', formData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['market-data', 'versions'] });
    },
  });
};
